/**
 * Date utility functions for the application.
 * This module provides a consistent API for date operations using date-fns.
 *
 * Key conventions:
 * - All dates are stored as ISO strings in UTC
 * - Display is always in local timezone
 * - Use these utilities instead of direct date-fns calls for consistency
 */

import {
  parseISO,
  formatISO,
  format,
  isValid,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  addDays,
  addMonths,
  addYears,
  subDays,
  subMonths,
  isSameDay,
  compareAsc,
  compareDesc
} from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';

// Get the local timezone
const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// ============================================================================
// Parsing - Convert various inputs to Date objects
// ============================================================================

/**
 * Normalize an ISO string to ensure it's treated as UTC.
 * If the string doesn't have a timezone indicator (Z or +/-offset),
 * append 'Z' to treat it as UTC (matching the previous Luxon behavior).
 */
const normalizeToUtc = (isoString: string): string => {
  // Already has Z suffix
  if (isoString.endsWith('Z')) return isoString;

  // Already has timezone offset (e.g., +00:00, -05:00)
  if (/[+-]\d{2}:\d{2}$/.test(isoString)) return isoString;

  // No timezone info - treat as UTC by appending Z
  return isoString + 'Z';
};

/**
 * Parse an ISO string to a Date object.
 * Strings without timezone info are treated as UTC.
 * Returns null if the input is invalid.
 */
export const parseIsoToDate = (isoString: string | null | undefined): Date | null => {
  if (!isoString) return null;
  const date = parseISO(normalizeToUtc(isoString));
  return isValid(date) ? date : null;
};

/**
 * Parse an ISO string (stored in UTC) and convert to local timezone for display.
 * Strings without timezone info are treated as UTC.
 * The returned Date represents the same instant but with local timezone awareness.
 */
export const parseIsoUtcToLocalDate = (isoString: string | null | undefined): Date | null => {
  if (!isoString) return null;
  const date = parseISO(normalizeToUtc(isoString));
  if (!isValid(date)) return null;
  return toZonedTime(date, localTimeZone);
};

// ============================================================================
// Formatting - Convert Date objects to strings
// ============================================================================

/**
 * Format a Date to ISO string in UTC (with Z suffix).
 * This is the standard format for storing dates and API calls.
 */
export const toIsoUtc = (date: Date | null | undefined): string | null => {
  if (!date || !isValid(date)) return null;
  return date.toISOString();
};

/**
 * Format a Date to ISO date string (YYYY-MM-DD) in UTC.
 * Useful for date-only values.
 */
export const toIsoDateUtc = (date: Date | null | undefined): string | null => {
  if (!date || !isValid(date)) return null;
  return date.toISOString().split('T')[0];
};

/**
 * Convert a local date to UTC and format as ISO string.
 * Use this when you have a local date (e.g., from a date picker) that needs to be stored as UTC.
 */
export const localDateToIsoUtc = (localDate: Date | null | undefined): string | null => {
  if (!localDate || !isValid(localDate)) return null;
  const utcDate = fromZonedTime(localDate, localTimeZone);
  return utcDate.toISOString();
};

/**
 * Format a date for display with date and optionally time.
 * Converts to local timezone for display.
 * ISO strings without timezone info are treated as UTC.
 * @param date - Date object or ISO string
 * @param hasTime - Whether to include time in the output
 * @returns Formatted string like "10.12.2025" or "10.12.2025 14:30"
 */
export const formatLocalDateTime = (
  date: Date | string | null | undefined,
  hasTime: boolean
): string => {
  if (!date) return '';

  // Parse string, treating strings without timezone as UTC
  const dateObj = typeof date === 'string' ? parseISO(normalizeToUtc(date)) : date;
  if (!isValid(dateObj)) return '';

  // Convert to local timezone for display
  const localDate = toZonedTime(dateObj, localTimeZone);

  return hasTime
    ? format(localDate, 'dd.MM.yyyy HH:mm')
    : format(localDate, 'dd.MM.yyyy');
};

/**
 * Format a date for display (date only, no time).
 * @param date - Date object or ISO string
 * @returns Formatted string like "10.12.2025"
 */
export const formatLocalDate = (date: Date | string | null | undefined): string => {
  return formatLocalDateTime(date, false);
};

/**
 * Format a date for display with time.
 * @param date - Date object or ISO string
 * @returns Formatted string like "10.12.2025 14:30"
 */
export const formatLocalDateTimeWithTime = (date: Date | string | null | undefined): string => {
  return formatLocalDateTime(date, true);
};

/**
 * Convert a date to a string suitable for the DateTimePicker component.
 * ISO strings without timezone info are treated as UTC.
 * @param date - Date object or ISO string
 * @param hasTime - Whether the time component is significant
 * @returns ISO string or null
 */
export const toDateTimePickerString = (
  date: Date | string | null | undefined,
  hasTime: boolean
): string | null => {
  if (!date) return null;

  // Handle Date object
  if (date instanceof Date) {
    if (!isValid(date)) return null;
    return hasTime ? formatISO(date) : formatISO(date, { representation: 'date' });
  }

  // Handle ISO string - normalize and pass through
  if (typeof date === 'string') {
    const normalized = normalizeToUtc(date);
    const parsed = parseISO(normalized);
    if (!isValid(parsed)) return null;
    return hasTime ? normalized : formatISO(parsed, { representation: 'date' });
  }

  return null;
};

// ============================================================================
// Date manipulation - Start/end of periods, arithmetic
// ============================================================================

/**
 * Helper to parse a date input, treating ISO strings without timezone as UTC.
 */
const parseDate = (date: Date | string): Date => {
  return typeof date === 'string' ? parseISO(normalizeToUtc(date)) : date;
};

/**
 * Get the start of day for a date in local timezone, returned as UTC.
 */
export const startOfDayLocal = (date: Date | string | null | undefined): Date | null => {
  if (!date) return null;
  const dateObj = parseDate(date);
  if (!isValid(dateObj)) return null;

  const localDate = toZonedTime(dateObj, localTimeZone);
  const startLocal = startOfDay(localDate);
  return fromZonedTime(startLocal, localTimeZone);
};

/**
 * Get the end of day for a date in local timezone, returned as UTC.
 */
export const endOfDayLocal = (date: Date | string | null | undefined): Date | null => {
  if (!date) return null;
  const dateObj = parseDate(date);
  if (!isValid(dateObj)) return null;

  const localDate = toZonedTime(dateObj, localTimeZone);
  const endLocal = endOfDay(localDate);
  return fromZonedTime(endLocal, localTimeZone);
};

/**
 * Get the start of month for a date in local timezone, returned as UTC.
 */
export const startOfMonthLocal = (date: Date | string | null | undefined): Date | null => {
  if (!date) return null;
  const dateObj = parseDate(date);
  if (!isValid(dateObj)) return null;

  const localDate = toZonedTime(dateObj, localTimeZone);
  const startLocal = startOfMonth(localDate);
  return fromZonedTime(startLocal, localTimeZone);
};

/**
 * Get the end of month for a date in local timezone, returned as UTC.
 */
export const endOfMonthLocal = (date: Date | string | null | undefined): Date | null => {
  if (!date) return null;
  const dateObj = parseDate(date);
  if (!isValid(dateObj)) return null;

  const localDate = toZonedTime(dateObj, localTimeZone);
  const endLocal = endOfMonth(localDate);
  return fromZonedTime(endLocal, localTimeZone);
};

/**
 * Add days to a date.
 */
export const addDaysToDate = (date: Date | string | null | undefined, days: number): Date | null => {
  if (!date) return null;
  const dateObj = parseDate(date);
  if (!isValid(dateObj)) return null;
  return addDays(dateObj, days);
};

/**
 * Subtract days from a date.
 */
export const subDaysFromDate = (date: Date | string | null | undefined, days: number): Date | null => {
  if (!date) return null;
  const dateObj = parseDate(date);
  if (!isValid(dateObj)) return null;
  return subDays(dateObj, days);
};

/**
 * Add months to a date.
 */
export const addMonthsToDate = (date: Date | string | null | undefined, months: number): Date | null => {
  if (!date) return null;
  const dateObj = parseDate(date);
  if (!isValid(dateObj)) return null;
  return addMonths(dateObj, months);
};

/**
 * Subtract months from a date.
 */
export const subMonthsFromDate = (date: Date | string | null | undefined, months: number): Date | null => {
  if (!date) return null;
  const dateObj = parseDate(date);
  if (!isValid(dateObj)) return null;
  return subMonths(dateObj, months);
};

/**
 * Add years to a date.
 */
export const addYearsToDate = (date: Date | string | null | undefined, years: number): Date | null => {
  if (!date) return null;
  const dateObj = parseDate(date);
  if (!isValid(dateObj)) return null;
  return addYears(dateObj, years);
};

/**
 * Subtract years from a date and return as ISO UTC string.
 */
export const subYearsFromDate = (date: Date | string | null | undefined, years: number): string | null => {
  if (!date) return null;
  const dateObj = parseDate(date);
  if (!isValid(dateObj)) return null;
  return addYears(dateObj, -years).toISOString();
};

// ============================================================================
// Comparison and validation
// ============================================================================

/**
 * Check if a date is valid.
 */
export const isValidDate = (date: Date | string | null | undefined): boolean => {
  if (!date) return false;
  const dateObj = parseDate(date);
  return isValid(dateObj);
};

/**
 * Check if two dates represent the same day (ignoring time).
 */
export const isSameDayDate = (
  date1: Date | string | null | undefined,
  date2: Date | string | null | undefined
): boolean => {
  if (!date1 || !date2) return false;
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);
  if (!isValid(d1) || !isValid(d2)) return false;
  return isSameDay(d1, d2);
};

/**
 * Compare two dates for sorting (ascending order).
 * Returns negative if date1 < date2, positive if date1 > date2, 0 if equal.
 */
export const compareDatesAsc = (
  date1: Date | string | null | undefined,
  date2: Date | string | null | undefined
): number => {
  if (!date1 && !date2) return 0;
  if (!date1) return -1;
  if (!date2) return 1;

  const d1 = parseDate(date1);
  const d2 = parseDate(date2);

  if (!isValid(d1) && !isValid(d2)) return 0;
  if (!isValid(d1)) return -1;
  if (!isValid(d2)) return 1;

  return compareAsc(d1, d2);
};

/**
 * Compare two dates for sorting (descending order).
 * Returns positive if date1 < date2, negative if date1 > date2, 0 if equal.
 */
export const compareDatesDesc = (
  date1: Date | string | null | undefined,
  date2: Date | string | null | undefined
): number => {
  if (!date1 && !date2) return 0;
  if (!date1) return 1;
  if (!date2) return -1;

  const d1 = parseDate(date1);
  const d2 = parseDate(date2);

  if (!isValid(d1) && !isValid(d2)) return 0;
  if (!isValid(d1)) return 1;
  if (!isValid(d2)) return -1;

  return compareDesc(d1, d2);
};

// ============================================================================
// Current time helpers
// ============================================================================

/**
 * Get the current date/time as a Date object.
 */
export const now = (): Date => new Date();

/**
 * Get the current date/time as an ISO string in UTC.
 */
export const nowIsoUtc = (): string => new Date().toISOString();

/**
 * Get the start of today in local timezone, returned as UTC Date.
 */
export const startOfToday = (): Date => {
  return startOfDayLocal(new Date())!;
};

/**
 * Get the end of today in local timezone, returned as UTC Date.
 */
export const endOfToday = (): Date => {
  return endOfDayLocal(new Date())!;
};

// ============================================================================
// Timezone conversion helpers
// ============================================================================

/**
 * Convert a UTC Date to local timezone for display.
 */
export const utcToLocal = (utcDate: Date | null | undefined): Date | null => {
  if (!utcDate || !isValid(utcDate)) return null;
  return toZonedTime(utcDate, localTimeZone);
};

/**
 * Convert a local Date to UTC for storage.
 */
export const localToUtc = (localDate: Date | null | undefined): Date | null => {
  if (!localDate || !isValid(localDate)) return null;
  return fromZonedTime(localDate, localTimeZone);
};

/**
 * Create a Date from local date/time components.
 * The resulting Date is in local timezone.
 */
export const fromLocalParts = (
  year: number,
  month: number, // 1-12 (not 0-indexed like JS Date)
  day: number,
  hour: number = 0,
  minute: number = 0,
  second: number = 0
): Date => {
  return new Date(year, month - 1, day, hour, minute, second);
};

/**
 * Create a UTC Date from date/time components.
 */
export const fromUtcParts = (
  year: number,
  month: number, // 1-12 (not 0-indexed like JS Date)
  day: number,
  hour: number = 0,
  minute: number = 0,
  second: number = 0
): Date => {
  return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
};

// ============================================================================
// Export local timezone for components that need it directly
// ============================================================================

export { localTimeZone };
